import React from 'react';
import { RefreshCallback } from '../../BlockFetcher';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Image as ImageType, ImageTypes } from '../../../types/api/ImageType';
import { ResponsiveType } from '../../../components/molecules/Image';
import { Strong } from '../../../components/atoms/Strong/Strong';
import {
  CheckBoxParam,
  Form,
  HiddenParam,
  ImageUploadWithCropping,
  RadioParam,
  SonForm,
  SubmitButton,
  TextParam,
  useForm,
} from '../../Form2';
import { formParamsUtils } from '../../Form2/utils/formParamsUtils';
import { AspectRatioOption } from '../../../components/molecules/ImageCropper/ImageCropperAspectRatioOptions';
import { NestedParam } from '../../Form2/types/NestedParam';
import { ImageFileParam } from '../../Form2/types/FileUploadParam';
import { useDelayed } from '../../../hooks/useDelayed';

type ImageUploadProps = ImageUploadFormTemplateProps & {
  form: SonForm<NestedParam<'imageuploadparams',
    'imageupload',
    | ImageFileParam<'image'>
    | HiddenParam<'itemId'>
    | HiddenParam<'width'>
    | HiddenParam<'height'>
    | HiddenParam<'y'>
    | HiddenParam<'x'>
    | TextParam<'alt'>
    | TextParam<'copyright'>
    | TextParam<'caption'>
    | CheckBoxParam<'popUp'>
    | RadioParam<'size', 'large' | 'medium' | 'small'>>>;
  formId: string;
  refresh: RefreshCallback;
};

type ImageUploadFormTemplateProps = {
  imageDescriptionName: string;
  imageType: ImageTypes;
  uuid: string;
  cropping: boolean;
  responsiveType?: ResponsiveType;
  viewportSwitch?: boolean;
  initialImage?: ImageType;
  additionalDescription?: string;
  /**
   * Optional list of aspect ratios that the user may choose from (when no hard constraints are enforced by the BE).
   * Displayed as radio buttons which set the suitable cropping rectangle on the croppie.
   */
  userSelectableAspectRatios?: AspectRatioOption[];
  selectedAspectRatio?: string;
};

export function ImageUploadForm({
  form,
  refresh,
  formId,
  imageDescriptionName,
  viewportSwitch,
  userSelectableAspectRatios,
  cropping,
  responsiveType,
}: ImageUploadProps) {
  const { t } = useSonTranslation('ImageUpload');
  const { formProps, fetchState, params } = useForm({ form, onSuccess: useDelayed(refresh) });
  const imageUploadParams = params.getByName('imageuploadparams');
  const imageParam = formParamsUtils(imageUploadParams).getByName('image');

  return (
    <div className="image-upload">
      <Form id={formId} modifier="no-padding" {...formProps}>
        <>
          <ImageUploadWithCropping
            param={imageUploadParams}
            userSelectableAspectRatios={userSelectableAspectRatios}
            viewportSwitch={viewportSwitch}
            cropping={cropping}
            responsiveType={responsiveType}
          />
          <SubmitButton
            buttonLabel={t('save')}
            fetchState={fetchState}
            messageFail={t('uploadFail.general', { imageDescriptionName })}
            messageSending={
              <>
                {t('uploadInProgress')}
                <Strong> {t(`phrases:pleaseWait`)}</Strong>
              </>
            }
            messageSuccess={t('uploadSuccess', { imageDescriptionName })}
            customErrorTypeMessages={{
              invalidFile: t('uploadFail.invalidFile'),
              invalidColorSpace: t('uploadFail.invalidColorSpace'),
              invalidOrientation: t('uploadFail.invalidOrientation'),
              invalidImageDimension: t('uploadFail.invalidImageDimension', {
                maxWidth: imageParam.constraints?.maxWidth,
                maxHeight: imageParam.constraints?.maxHeight,
              }),
            }}
          />
        </>
      </Form>
    </div>
  );
}
